import { nettoVon, regelFinden } from "./rechnen";
import {
  EINNAHME,
  cent,
  type Beleg,
  type Buchung,
  type Konto,
  type Regel,
  type Steuerjahr,
  type Teil,
} from "./typen";

const MAX_TAGE = 45;

const abstand = (a: string, b: string) =>
  Math.abs(new Date(a).getTime() - new Date(b).getTime()) / 86400000;

// Passende Kontozeilen: gleicher Betrag, gleiche Richtung, noch ohne Beleg, nächstes Datum zuerst.
export const vorschlaege = (
  beleg: Beleg,
  buchungen: Buchung[],
  belege: Beleg[],
  konten: Konto[],
) => {
  if (!beleg.brutto) return [];
  const belegt = new Set(belege.filter((b) => !b.verworfen).map((b) => b.buchung_id));
  const ziel = (beleg.richtung === "ausgabe" ? -1 : 1) * cent(beleg.brutto);
  const partner = beleg.partner.trim().toLowerCase();
  const kontoIds = new Set(konten.map((k) => k.id));
  return buchungen
    .filter(
      (b) =>
        b.konto_id !== null &&
        kontoIds.has(b.konto_id) &&
        !belegt.has(b.id) &&
        cent(b.betrag) === ziel &&
        (!beleg.datum || abstand(b.datum, beleg.datum) <= MAX_TAGE),
    )
    .map((b) => ({
      buchung: b,
      tage: beleg.datum ? abstand(b.datum, beleg.datum) : 0,
      partnerPasst: !!partner && b.partner.toLowerCase().includes(partner),
    }))
    .sort(
      (a, b) =>
        Number(b.partnerPasst) - Number(a.partnerPasst) || a.tage - b.tage,
    );
};

export const vorbelegung = (
  beleg: Beleg,
  partner: string,
  regeln: Regel[],
  jahr: Steuerjahr,
) => {
  const regel = regelFinden(partner, "", regeln);
  return {
    kategorie:
      beleg.kategorie ??
      (beleg.richtung === "einnahme" ? EINNAHME : (regel?.kategorie ?? "")),
    satz: beleg.satz ?? regel?.satz ?? jahr.satz_regel,
  };
};

// Was aus den Eingaben auf der Buchung landet. Mischrechnung: Anteil zum ermäßigten Satz, der Rest regulär.
export const buchungsWerte = (
  beleg: Beleg,
  kategorie: string,
  satz: number,
  jahr: Steuerjahr,
) => {
  const vorzeichen = beleg.richtung === "ausgabe" ? -1 : 1;
  const brutto = cent(beleg.brutto ?? 0);
  const ermaessigt = cent(beleg.brutto_ermaessigt ?? 0);
  const teile: Teil[] | null =
    ermaessigt > 0 && ermaessigt < brutto
      ? [
          {
            betrag: (vorzeichen * ermaessigt) / 100,
            kategorie,
            satz: jahr.satz_ermaessigt,
          },
          {
            betrag: (vorzeichen * (brutto - ermaessigt)) / 100,
            kategorie,
            satz: jahr.satz_regel,
          },
        ]
      : null;
  return {
    betrag: (vorzeichen * brutto) / 100,
    kategorie,
    satz,
    anteil: 100,
    teile,
    beleg: beleg.nummer || beleg.dateiname,
  };
};

// USt bzw. Vorsteuer, die sich aus den Eingaben ergibt
export const steuerAusBeleg = (
  beleg: Beleg,
  satz: number,
  jahr: Steuerjahr,
) => {
  const brutto = cent(beleg.brutto ?? 0);
  const ermaessigt = cent(beleg.brutto_ermaessigt ?? 0);
  if (ermaessigt > 0 && ermaessigt < brutto) {
    const rest = brutto - ermaessigt;
    return (
      ermaessigt -
      nettoVon(ermaessigt, jahr.satz_ermaessigt) +
      rest -
      nettoVon(rest, jahr.satz_regel)
    );
  }
  return brutto - nettoVon(brutto, satz);
};
